import { saveAs } from 'file-saver';
import {
  Document, Packer, Paragraph, TextRun, HeadingLevel,
  PageBreak, BookmarkStart, BookmarkEnd, InternalHyperlink,
} from 'docx';
import jsPDF from 'jspdf';

// ─── ProseMirror JSON → flat block list ────────────────────────────────────

interface FlatBlock {
  type: 'h1' | 'h2' | 'h3' | 'paragraph' | 'bullet' | 'ordered';
  runs: Array<{ text: string; bold?: boolean; italic?: boolean; underline?: boolean; strikethrough?: boolean }>;
  listIndex?: number;
}

function flattenDoc(docJson: any): FlatBlock[] {
  const blocks: FlatBlock[] = [];

  function extractRuns(content: any[]): FlatBlock['runs'] {
    if (!content) return [];
    return content.map((node: any) => {
      const marks = node.marks ?? [];
      return {
        text: node.text ?? '',
        bold: marks.some((m: any) => m.type === 'bold'),
        italic: marks.some((m: any) => m.type === 'italic'),
        underline: marks.some((m: any) => m.type === 'underline'),
        strikethrough: marks.some((m: any) => m.type === 'strike'),
      };
    });
  }

  function walk(nodes: any[], listType?: 'bullet' | 'ordered', listIdx?: number) {
    for (const node of nodes ?? []) {
      switch (node.type) {
        case 'heading': {
          const level = node.attrs?.level ?? 2;
          const type = level === 1 ? 'h1' : level === 2 ? 'h2' : 'h3';
          blocks.push({ type, runs: extractRuns(node.content) });
          break;
        }
        case 'paragraph':
          if (listType) {
            blocks.push({ type: listType, runs: extractRuns(node.content), listIndex: listIdx });
          } else {
            blocks.push({ type: 'paragraph', runs: extractRuns(node.content) });
          }
          break;
        case 'bulletList':
          (node.content ?? []).forEach((li: any, i: number) => walk(li.content ?? [], 'bullet', i + 1));
          break;
        case 'orderedList':
          (node.content ?? []).forEach((li: any, i: number) => walk(li.content ?? [], 'ordered', i + 1));
          break;
        case 'listItem':
          walk(node.content ?? [], listType, listIdx);
          break;
        case 'horizontalRule':
          // Skip — keep the export clean
          break;
        case 'blockquote':
          walk(node.content ?? []);
          break;
        default:
          if (node.content) walk(node.content);
      }
    }
  }

  walk(docJson.content ?? []);
  return blocks;
}

/** Create a bookmark-safe anchor ID from heading text. */
function toAnchorId(text: string): string {
  return text.replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_').substring(0, 40);
}

/** Get plain text from a block's runs. */
function blockText(block: FlatBlock): string {
  return block.runs.map(r => r.text).join('');
}

// ─── Export to Word (.docx) ────────────────────────────────────────────────

export async function exportToWord(docJson: any, title: string) {
  const blocks = flattenDoc(docJson);

  // Collect headings for Table of Contents (with unique numeric IDs for bookmarks)
  let bookmarkIdCounter = 1;
  const tocEntries: { text: string; level: number; anchor: string; linkId: number }[] = [];
  for (const block of blocks) {
    if (block.type === 'h1' || block.type === 'h2' || block.type === 'h3') {
      const text = blockText(block);
      if (text) {
        tocEntries.push({
          text,
          level: block.type === 'h1' ? 1 : block.type === 'h2' ? 2 : 3,
          anchor: toAnchorId(text),
          linkId: bookmarkIdCounter++,
        });
      }
    }
  }

  // Build a lookup from anchor → linkId for the body pass
  const anchorToLinkId = new Map(tocEntries.map(e => [e.anchor, e.linkId]));

  // ── Build Table of Contents page ──
  const tocChildren: Paragraph[] = [];

  // TOC title
  tocChildren.push(new Paragraph({
    children: [new TextRun({ text: 'Table of Contents', bold: true, size: 28, font: 'Calibri' })],
    heading: HeadingLevel.HEADING_1,
    spacing: { after: 200 },
  }));

  // TOC entries as internal hyperlinks
  for (const entry of tocEntries) {
    const indent = entry.level === 1 ? 0 : entry.level === 2 ? 360 : 720;
    const fontSize = entry.level === 1 ? 22 : 20;
    const isBold = entry.level <= 2;

    tocChildren.push(new Paragraph({
      children: [
        new InternalHyperlink({
          anchor: entry.anchor,
          children: [
            new TextRun({
              text: entry.text,
              bold: isBold,
              size: fontSize,
              font: 'Calibri',
              color: '2B579A',
            }),
          ],
        }),
      ],
      spacing: { before: entry.level === 1 ? 120 : 40, after: 40 },
      indent: indent > 0 ? { left: indent } : undefined,
    }));
  }

  // Page break after TOC
  tocChildren.push(new Paragraph({
    children: [new PageBreak()],
  }));

  // ── Build document body ──
  const bodyChildren: Paragraph[] = [];

  for (const block of blocks) {
    const text = blockText(block);
    const isHeading = block.type === 'h1' || block.type === 'h2' || block.type === 'h3';

    const textRuns = block.runs.map(
      (r) =>
        new TextRun({
          text: r.text,
          bold: r.bold || isHeading,
          italics: r.italic,
          underline: r.underline ? {} : undefined,
          strike: r.strikethrough,
          size: block.type === 'h1' ? 28 : block.type === 'h2' ? 24 : block.type === 'h3' ? 22 : 20,
          font: 'Calibri',
        }),
    );

    switch (block.type) {
      case 'h1': {
        const anchor = toAnchorId(text);
        const lid = anchorToLinkId.get(anchor) ?? 0;
        bodyChildren.push(new Paragraph({
          children: [new BookmarkStart(anchor, lid), ...textRuns, new BookmarkEnd(lid)],
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 360, after: 120 },
        }));
        break;
      }
      case 'h2': {
        const anchor = toAnchorId(text);
        const lid = anchorToLinkId.get(anchor) ?? 0;
        bodyChildren.push(new Paragraph({
          children: [new BookmarkStart(anchor, lid), ...textRuns, new BookmarkEnd(lid)],
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 280, after: 80 },
        }));
        break;
      }
      case 'h3': {
        const anchor = toAnchorId(text);
        const lid = anchorToLinkId.get(anchor) ?? 0;
        bodyChildren.push(new Paragraph({
          children: [new BookmarkStart(anchor, lid), ...textRuns, new BookmarkEnd(lid)],
          heading: HeadingLevel.HEADING_3,
          spacing: { before: 200, after: 60 },
        }));
        break;
      }
      case 'bullet':
        bodyChildren.push(new Paragraph({
          children: textRuns,
          bullet: { level: 0 },
          spacing: { before: 40, after: 40 },
        }));
        break;
      case 'ordered':
        bodyChildren.push(new Paragraph({
          children: [new TextRun({ text: `${block.listIndex}. `, font: 'Calibri', size: 20 }), ...textRuns],
          spacing: { before: 40, after: 40 },
          indent: { left: 360 },
        }));
        break;
      default:
        // Skip empty paragraphs
        if (!text.trim()) {
          bodyChildren.push(new Paragraph({ children: [], spacing: { before: 60, after: 60 } }));
        } else {
          bodyChildren.push(new Paragraph({
            children: textRuns,
            spacing: { before: 60, after: 60 },
          }));
        }
    }
  }

  const doc = new Document({
    sections: [{
      properties: {
        page: {
          margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 },
        },
      },
      children: [...tocChildren, ...bodyChildren],
    }],
  });

  const blob = await Packer.toBlob(doc);
  const filename = sanitiseFilename(title) + '.docx';
  saveAs(blob, filename);
}

// ─── Export to PDF ─────────────────────────────────────────────────────────

export function exportToPdf(docJson: any, title: string) {
  const blocks = flattenDoc(docJson);
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });

  const PAGE_W = 210;
  const MARGIN_L = 20;
  const MARGIN_R = 20;
  const MARGIN_T = 25;
  const MARGIN_B = 20;
  const CONTENT_W = PAGE_W - MARGIN_L - MARGIN_R;
  const PAGE_H = 297;

  let y = MARGIN_T;

  function ensureSpace(needed: number) {
    if (y + needed > PAGE_H - MARGIN_B) {
      pdf.addPage();
      y = MARGIN_T;
    }
  }

  function drawWrappedText(
    text: string,
    x: number,
    fontSize: number,
    fontStyle: string,
    lineHeight: number,
    maxWidth: number,
  ) {
    pdf.setFontSize(fontSize);
    pdf.setFont('helvetica', fontStyle);
    const lines = pdf.splitTextToSize(text, maxWidth);
    for (const line of lines) {
      ensureSpace(lineHeight);
      pdf.text(line, x, y);
      y += lineHeight;
    }
  }

  // ── Table of Contents page ──
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Table of Contents', MARGIN_L, y);
  y += 10;

  const tocHeadings = blocks.filter(b => b.type === 'h1' || b.type === 'h2' || b.type === 'h3');
  for (const heading of tocHeadings) {
    const text = blockText(heading);
    if (!text) continue;
    const indent = heading.type === 'h1' ? 0 : heading.type === 'h2' ? 5 : 10;
    const fontSize = heading.type === 'h1' ? 11 : 10;
    const style = heading.type === 'h1' || heading.type === 'h2' ? 'bold' : 'normal';
    ensureSpace(5);
    drawWrappedText(text, MARGIN_L + indent, fontSize, style, 5, CONTENT_W - indent);
    y += 1;
  }

  // New page for content
  pdf.addPage();
  y = MARGIN_T;

  for (const block of blocks) {
    const text = blockText(block);
    if (!text) continue;

    switch (block.type) {
      case 'h1':
        ensureSpace(12);
        y += 4;
        drawWrappedText(text, MARGIN_L, 16, 'bold', 7, CONTENT_W);
        y += 2;
        break;
      case 'h2':
        ensureSpace(10);
        y += 3;
        drawWrappedText(text, MARGIN_L, 13, 'bold', 6, CONTENT_W);
        y += 1.5;
        break;
      case 'h3':
        ensureSpace(8);
        y += 2;
        drawWrappedText(text, MARGIN_L, 11, 'bold', 5.5, CONTENT_W);
        y += 1;
        break;
      case 'bullet':
        ensureSpace(5);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text('\u2022', MARGIN_L + 2, y);
        drawWrappedText(text, MARGIN_L + 7, 10, 'normal', 4.5, CONTENT_W - 7);
        break;
      case 'ordered':
        ensureSpace(5);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`${block.listIndex}.`, MARGIN_L + 1, y);
        drawWrappedText(text, MARGIN_L + 7, 10, 'normal', 4.5, CONTENT_W - 7);
        break;
      default: {
        ensureSpace(5);
        const hasBold = block.runs.some((r) => r.bold);
        const hasItalic = block.runs.some((r) => r.italic);
        const style = hasBold && hasItalic ? 'bolditalic' : hasBold ? 'bold' : hasItalic ? 'italic' : 'normal';
        drawWrappedText(text, MARGIN_L, 10, style, 4.5, CONTENT_W);
        y += 1.5;
      }
    }
  }

  const filename = sanitiseFilename(title) + '.pdf';
  pdf.save(filename);
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function sanitiseFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9\s\-_().]/g, '').replace(/\s+/g, ' ').trim() || 'report';
}
